import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { omit } from '../../util/common';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
  parseSearchPreferences,
  rerankBySearchPreferences,
  preferenceMatchSummary,
  boundsCenter,
} from '../../util/search';
import { logSearchEvent, logFunnelEvent, getUserLocation } from '../../util/analytics';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
// StyleFilters is no longer used here — SearchSidebar's
// Nice-to-have section renders the aesthetic chips inline (Stream 3
// PR-4e). The component remains in the codebase for any future
// re-use; just unimported.
import SubThemeFilters from './SubThemeFilters/SubThemeFilters';
import SearchSidebar from './SearchSidebar/SearchSidebar';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import LocationFilter from './LocationFilter/LocationFilter';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchResultsContextBar from './SearchResultsContextBar/SearchResultsContextBar';
// LocationBar import dropped — its only use was inside the now-
// replaced aside (Stream 3 PR-4e).

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// Resolve the "current occasion" from the URL for breadcrumb /
// sub-theme display. pub_occasions can be a comma-separated list
// (multi-enum OR filter); the first element drives display since
// downstream components expect a single key.
const firstOccasionFromQuery = (validQueryParams = {}, searchParamsInURL = {}) => {
  const raw =
    validQueryParams.pub_occasions ||
    validQueryParams.pub_categoryLevel1 ||
    searchParamsInURL.pub_occasions ||
    searchParamsInURL.pub_categoryLevel1 ||
    null;
  if (!raw) return null;
  return typeof raw === 'string' ? raw.split(',')[0].trim() : raw;
};

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      // Search analytics (ported from SearchPageWithMap with the grid
      // switch — see docs/design/grid-search-variant-switch.md). userLocation
      // feeds metro/lat/lng into the search-event payload; searchExecuted
      // de-dupes the per-result-load funnel event.
      userLocation: null,
      searchExecuted: false,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    
    // SubTheme filter
    this.handleSubThemeFilter = this.handleSubThemeFilter.bind(this);

    // Location (Must-have) filter + analytics
    this.handleLocationChange = this.handleLocationChange.bind(this);
    this.trackSearchEvent = this.trackSearchEvent.bind(this);

    // Active-keyword chip (audit P1: keyword searches were unclearable)
    this.handleClearKeyword = this.handleClearKeyword.bind(this);
  }

  componentDidMount() {
    // Load saved user location on mount (feeds search-event analytics).
    const savedLocation = getUserLocation();
    if (savedLocation) {
      this.setState({ userLocation: savedLocation });
    }
  }

  componentDidUpdate(prevProps) {
    // Track a search event once results finish loading for the current
    // params. Mirrors SearchPageWithMap: reset the flag when params change,
    // then fire once when results land.
    const { searchParams, listings, searchInProgress } = this.props;
    const { searchExecuted } = this.state;

    const searchParamsChanged =
      JSON.stringify(prevProps.searchParams) !== JSON.stringify(searchParams);
    const hasResults = !searchInProgress && listings && listings.length >= 0;

    if (searchParamsChanged) {
      if (searchExecuted) {
        this.setState({ searchExecuted: false });
      }
    } else if (hasResults && !searchExecuted) {
      this.trackSearchEvent();
      this.setState({ searchExecuted: true });
      logFunnelEvent('search_executed');
    }
  }

  // Location (Must-have) filter — sets the `bounds` param so Sharetribe filters
  // results to the chosen area (kits whose geolocation is outside the box drop
  // out — that's the acceptance test). Mirrors the map's onMapMoveEnd: parse the
  // current URL, drop the stale address/bounds + page, then push the new bounds
  // (or omit both on clear).
  handleLocationChange({ bounds, address }) {
    const { history, routeConfiguration, location } = this.props;
    const parsed = parse(location.search, { latlng: ['origin'], latlngBounds: ['bounds'] });
    const rest = omit(parsed, ['address', 'bounds', 'page']);
    const queryParams = bounds ? { ...rest, address, bounds } : rest;
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  // Drop the keywords param (and stale pagination) — same URL-push
  // pattern as handleLocationChange above. Backs the “keyword ×” chip.
  handleClearKeyword() {
    const { history, routeConfiguration, location } = this.props;
    const parsed = parse(location.search, { latlng: ['origin'], latlngBounds: ['bounds'] });
    const queryParams = omit(parsed, ['keywords', 'page']);
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  trackSearchEvent() {
    const { listings, location } = this.props;
    // Prefer the freshest cached location — the Location filter writes it to
    // localStorage on use, so this reflects an in-session location change that
    // hasn't been re-read into component state yet.
    const userLocation = getUserLocation() || this.state.userLocation;

    const queryParams = parse(location.search);

    const searchEventData = {
      metro: userLocation?.city || '',
      lat: userLocation?.lat || null,
      lng: userLocation?.lng || null,
      category: queryParams.pub_categoryLevel1 || queryParams.category || '',
      subTheme: queryParams.pub_subTheme || '',
      dateStart: queryParams.dates ? queryParams.dates.split(',')[0] : null,
      dateEnd: queryParams.dates ? queryParams.dates.split(',')[1] : null,
      radiusMiles: userLocation?.radius || 15,
      resultCount: listings ? listings.length : 0,
    };

    logSearchEvent(searchEventData);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, filterQueryParamNames);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  handleSubThemeFilter(filterKey, value) {
    // Pass an explicit `null` through for the clear path so
    // getHandleChangedValueFn drops the key rather than retaining the stale
    // urlQueryParams value. Pre-fix, `{}` left the old value in place and the
    // Nice-to-have Clear path silently no-op'd (PR-4d; ported here with the
    // grid switch — see docs/design/grid-search-variant-switch.md).
    const updatedParams =
      value != null ? { [filterKey]: value } : { [filterKey]: null };
    const handleChangedValueFn = this.getHandleChangedValueFn(true);
    handleChangedValueFn(updatedParams);
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings: rawListings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    // Nice-to-have preference re-rank (pref_* URL params). Mirrors
    // SearchPageWithMap: Sharetribe returns a relevance-ordered list and we
    // re-sort it client-side so listings matching more preferences float up
    // (capped boost; preferences nudge, they don't narrow the result set).
    // Ported with the grid switch — see docs/design/grid-search-variant-switch.md.
    const activePreferences = parseSearchPreferences(parse(location.search));
    const listings = rerankBySearchPreferences(rawListings, activePreferences);
    const prefMatchSummary = preferenceMatchSummary(listings, activePreferences);

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig, mainSearch } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
      mainSearch,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      mainSearch,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    // Drop the legacy occasion filters from the sidebar:
    //   - `event_type` (custom listing field, Console-defined): old
    //     pre-PR-180 enum + duplicates the new occasions filter.
    //   - `categoryLevel` (built-in Sharetribe nested category):
    //     Console-side schema still holds the pre-PR-180 enum + has
    //     a broken categoryLevel2 (e.g. "Boho" registered as a
    //     subcategory of Baby Shower). Bug report: clicking
    //     "Baby Shower" in the sidebar showed Boho as a subcategory.
    // The custom `occasions` multi-enum field (registered in PR-187,
    // src/config/configListing.js, with `searchMode: has_any` for OR
    // matching) is the canonical occasion filter going forward — it
    // reads its 8 options from the same hardcoded list as the
    // homepage / wizard, so there's no Console drift.
    // The underlying publicData fields (event_type / categoryLevel1)
    // stay populated for back-compat (wizard mirrors them from
    // occasions[0]).
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ].filter(f => f.key !== 'event_type' && f.key !== 'categoryLevel');

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Parse page heading to be included in the title
    const pageHeading = searchInProgress
      ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
      : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      pageHeading
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          {/* Stream 3 PR-4e: two-tier filter sidebar.
              Replaces the previous always-hidden filter aside (which
              held only a Location filter that wasn't reachable on
              any breakpoint). The new sidebar shows on desktop only;
              mobile uses SearchFiltersMobile's bottom sheet. */}
          <aside
            className={css.layoutWrapperFilterColumn}
            data-testid="filterColumnAside"
          >
            <SearchSidebar
              primaryFilters={availableFilters}
              commonFilterProps={{
                listingCategories,
                marketplaceCurrency,
                urlQueryParams: validQueryParams,
                initialValues: initialValues(this.props, this.state.currentQueryParams),
                getHandleChangedValueFn: this.getHandleChangedValueFn,
                intl,
              }}
              location={location}
              onPreferenceChange={this.handleSubThemeFilter}
              onLocationChange={this.handleLocationChange}
            />
          </aside>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                {/* Location leads the mobile drawer (2026-07-02, audit
                    "location spine" P0): the filter existed only in the
                    desktop sidebar — phones had NO way to filter by area.
                    Same component + bounds plumbing as desktop. */}
                <LocationFilter
                  id="SearchFiltersMobile.location"
                  location={location}
                  onLocationChange={this.handleLocationChange}
                />
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPage_MobileFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                currentCategory={firstOccasionFromQuery(validQueryParams, searchParamsInURL)}
                activeKeyword={
                  typeof validQueryParams.keywords === 'string' && validQueryParams.keywords.trim()
                    ? validQueryParams.keywords.trim()
                    : null
                }
                onClearKeyword={this.handleClearKeyword}
              />

              {/* SubThemeFilters stays here as subcategory navigation
                  (not a filter facet). Mobile uses SearchFiltersMobile
                  bottom sheet for the full set. */}
              <SubThemeFilters
                location={location}
                onFilterClick={this.handleSubThemeFilter}
                currentCategory={firstOccasionFromQuery(validQueryParams, searchParamsInURL)}
                selectedSubTheme={validQueryParams.pub_subTheme}
              />

              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                {/* Nice-to-have preferences context bar (PR-4c). Renders only
                    when at least one pref_* param is set AND at least one
                    loaded listing matches. Ported with the grid switch. */}
                {listingsAreLoaded && prefMatchSummary.length > 0 && (
                  <SearchResultsContextBar
                    totalCount={totalItems}
                    matchSummary={prefMatchSummary}
                  />
                )}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  searchCenter={boundsCenter(validQueryParams.bounds)}
                  intl={intl}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
